import { useMsal } from '@azure/msal-react';
import { useGoogleLogin } from '@react-oauth/google';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Shared
import Configs from '@/Configs';
import { useAuth } from '@/common/appContext';
import { UnifiedIcon } from '@/components/UnifiedIcon';

// Relative imports
import { loginRequest } from '@/authConfig';
import {
  postSelfOnboardingSsoStart,
  type SelfOnboardingSsoStartResult,
} from './core/apiCalls';

export type SelfOnboardingSsoDraft = {
  signupTicket: string;
  email: string;
  firstName: string;
  lastName: string;
  provider: 'google' | 'microsoft';
};

type SelfOnboardingWorkAccountButtonsProps = {
  campaignSlug: string;
  disabled: boolean;
  onDraft: (draft: SelfOnboardingSsoDraft) => void;
  onMessage: (message: string | null) => void;
};

function isSsoDraft(value: unknown): value is SelfOnboardingSsoStartResult {
  return (
    typeof value === 'object' &&
    value !== null &&
    'signupTicket' in value &&
    typeof (value as SelfOnboardingSsoStartResult).signupTicket === 'string'
  );
}

/**
 * Google Workspace and Microsoft work-account buttons for the public signup form.
 * Hidden when the provider client id is not configured.
 */
export function SelfOnboardingWorkAccountButtons({
  campaignSlug,
  disabled,
  onDraft,
  onMessage,
}: SelfOnboardingWorkAccountButtonsProps) {
  const { t } = useTranslation();
  const { correlationId } = useAuth();
  const { instance } = useMsal();
  const [pending, setPending] = useState<'google' | 'microsoft' | null>(null);
  const showGoogle = Configs.googleSSOEnabled;
  const showMicrosoft = Configs.microsoftSsoEnabled;

  const applyStartResult = useCallback(
    (result: SelfOnboardingSsoStartResult | { code: string } | number) => {
      if (isSsoDraft(result)) {
        onMessage(null);
        onDraft({
          signupTicket: result.signupTicket,
          email: result.email,
          firstName: result.firstName,
          lastName: result.lastName,
          provider: result.provider,
        });
        return;
      }
      const code = typeof result === 'object' && result && 'code' in result ? result.code : '';
      if (code === 'WORK_ACCOUNT_REQUIRED') {
        onMessage(t('plg.selfOnboarding.error.workAccount'));
        return;
      }
      if (code === 'WORKSPACE_ALREADY_LINKED') {
        onMessage(t('plg.selfOnboarding.error.workspaceLinked'));
        return;
      }
      if (code === 'EMAIL_ALREADY_USED' || result === 409) {
        onMessage(t('plg.selfOnboarding.error.emailTaken'));
        return;
      }
      onMessage(t('plg.selfOnboarding.error.generic'));
    },
    [onDraft, onMessage, t]
  );

  const logInGoogle = useGoogleLogin({
    onSuccess: (codeResponse) => {
      const accessToken = codeResponse.access_token;
      void (async () => {
        try {
          const result = await postSelfOnboardingSsoStart(
            { provider: 'google', token: accessToken, campaign: campaignSlug },
            correlationId ?? null
          );
          applyStartResult(result);
        } catch {
          onMessage(t('plg.selfOnboarding.error.generic'));
        } finally {
          setPending(null);
        }
      })();
    },
    onError: () => {
      setPending(null);
      onMessage(t('plg.selfOnboarding.error.generic'));
    },
    onNonOAuthError: () => {
      setPending(null);
      onMessage(t('plg.selfOnboarding.error.generic'));
    },
  });

  const continueWithMicrosoft = useCallback(async () => {
    setPending('microsoft');
    onMessage(null);
    try {
      const result = await instance.loginPopup(loginRequest);
      const idToken = result.idToken;
      if (!idToken) {
        onMessage(t('plg.selfOnboarding.error.generic'));
        return;
      }
      const started = await postSelfOnboardingSsoStart(
        { provider: 'microsoft', token: idToken, campaign: campaignSlug },
        correlationId ?? null
      );
      applyStartResult(started);
    } catch {
      onMessage(t('plg.selfOnboarding.error.generic'));
    } finally {
      setPending(null);
    }
  }, [applyStartResult, campaignSlug, correlationId, instance, onMessage, t]);

  if (!showGoogle && !showMicrosoft) {
    return <div data-testid="plg-signup-sso" className="hidden" />;
  }

  return (
    <div data-testid="plg-signup-sso" className="flex w-full flex-col gap-3">
      {showGoogle ? (
        <button
          type="button"
          data-testid="plg-signup-google"
          className="flex w-full items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          disabled={disabled || pending !== null}
          onClick={() => {
            setPending('google');
            onMessage(null);
            logInGoogle();
          }}
        >
          <UnifiedIcon name="GoogleLogo" library="phosphor" weight="bold" className="h-5 w-5" />
          {t('plg.selfOnboarding.signup.google')}
        </button>
      ) : null}
      {showMicrosoft ? (
        <button
          type="button"
          data-testid="plg-signup-microsoft"
          className="flex w-full items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          disabled={disabled || pending !== null}
          onClick={() => {
            void continueWithMicrosoft();
          }}
        >
          <UnifiedIcon name="WindowsLogo" library="phosphor" className="h-5 w-5" />
          {t('plg.selfOnboarding.signup.microsoft')}
        </button>
      ) : null}
      <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-slate-400">
        <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
        {t('plg.selfOnboarding.signup.or')}
        <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
      </div>
    </div>
  );
}
